import { useMemo } from 'react';
import { Cable } from 'lucide-react';
import { getActiveDrillItemPath, getActiveNavItemLabel } from '../NavBar/nav-utils';
import type { NavItem } from '../NavBar/nav-types';
import {
  IdentityHeader,
  IdentityTile,
  TakeoverAside,
  takeoverShellClassName,
} from './TakeoverChrome';
import { TakeoverNavGroups, TakeoverSearchField, type TakeoverNavGroup } from './TakeoverNav';
import { useTakeoverNavSearch } from './useTakeoverNavSearch';

export interface ConnectShellViewProps {
  /** Organization named in the identity header. */
  orgName: string;
  /** Current pathname, used to highlight the active nav entry. */
  activePath: string;
  /** The Connect sidebar items (Credentials, Sharing, MCP, …). */
  items: NavItem[];
  onBack: () => void;
  children: React.ReactNode;
}

/**
 * Presentational layout of the Connect takeover: the shared aside chrome with
 * the Connect items as a single searchable group, beside the connect pages.
 * The connected {@link ConnectShell} wires nav sources, routing, and the
 * escape/back behavior; stories drive this view directly.
 */
export function ConnectShellView({
  orgName,
  activePath,
  items,
  onBack,
  children,
}: ConnectShellViewProps) {
  const { query, setQuery, filterGroups } = useTakeoverNavSearch();

  const allGroups = useMemo<TakeoverNavGroup[]>(() => [{ label: '', items }], [items]);
  const visibleGroups = filterGroups(allGroups);

  const activeItemPath = useMemo(
    () => getActiveDrillItemPath(items, activePath),
    [items, activePath],
  );
  const pageTitle = getActiveNavItemLabel(items, activePath, 'Connect');

  return (
    <div className={takeoverShellClassName} data-testid="connect-shell">
      <TakeoverAside
        back={{ label: 'Back', onClick: onBack, testId: 'connect-back' }}
        header={
          <IdentityHeader tile={<IdentityTile icon={Cable} />} title="Connect" subtitle={orgName} />
        }
        pageTitle={pageTitle}
        page={<div className="flex min-w-0 flex-1 flex-col overflow-hidden">{children}</div>}
      >
        <TakeoverSearchField query={query} setQuery={setQuery} placeholder="Search connect" />
        <nav
          aria-label="Connect navigation"
          className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-1.5 pb-3 scrollbar-fade"
        >
          <TakeoverNavGroups
            groups={visibleGroups}
            activeItemPath={activeItemPath}
            testIdPrefix="connect-nav"
          />
        </nav>
      </TakeoverAside>
    </div>
  );
}
